import { AxiosError } from 'axios';
import type { NormalizedApiError } from './types';

interface ApiErrorPayload {
	code?: string;
	message?: string;
	/** Problem Details (RFC 9457) human-readable explanation. */
	detail?: string;
	title?: string;
	errors?: Record<string, string>;
	requestId?: string;
}

/** Problem Details code of a request refused by the tenant's CMS IP allowlist. */
export const IP_NOT_ALLOWED = 'IP_NOT_ALLOWED';

export const IP_NOT_ALLOWED_MESSAGE =
	'Access from your current network is not allowed for this workspace. Connect from an approved network (for example the office or VPN) or contact your administrator.';

const NETWORK_ERROR_MESSAGE =
	'Unable to reach the server. Please check your connection and try again.';
const UNEXPECTED_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const SERVER_ERROR_MESSAGE =
	'Something went wrong on the server. Please try again later.';
const SERVER_UNAVAILABLE_MESSAGE =
	'The server is temporarily unavailable. Please try again in a moment.';

/**
 * Codes the backend falls back to when a failure has no specific code: an
 * unhandled exception (`INTERNAL_ERROR`) or a framework refusal (`HTTP_ERROR`).
 */
const GENERIC_ERROR_CODES: ReadonlySet<string> = new Set([
	'INTERNAL_ERROR',
	'HTTP_ERROR',
]);

/**
 * Text by HTTP status, used when the response has no usable `detail`. The
 * Problem Details `title` is never shown: the backend fills it with the status
 * name (e.g. `INTERNAL_SERVER_ERROR`), which is not meant for users.
 */
const STATUS_MESSAGES: Record<number, string> = {
	400: 'The request is not valid. Check the values and try again.',
	401: 'Your session has expired. Please sign in again.',
	403: 'You do not have permission to do this.',
	404: 'This item no longer exists, or you cannot access it.',
	408: 'The server took too long to respond. Please try again.',
	409: 'This item was changed in the meantime. Refresh and try again.',
	413: 'The request is too large.',
	422: 'The request could not be processed. Check the values and try again.',
	429: 'Too many requests. Wait a moment and try again.',
	500: SERVER_ERROR_MESSAGE,
	502: SERVER_UNAVAILABLE_MESSAGE,
	503: SERVER_UNAVAILABLE_MESSAGE,
	504: SERVER_UNAVAILABLE_MESSAGE,
};

function statusMessage(status: number): string {
	return (
		STATUS_MESSAGES[status] ??
		(status >= 500 ? SERVER_ERROR_MESSAGE : UNEXPECTED_ERROR_MESSAGE)
	);
}

function nonEmpty(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() ? value : undefined;
}

/** User-facing message of an HTTP error response. */
function responseMessage(
	status: number,
	code: string | null,
	payload: ApiErrorPayload | undefined,
): string {
	if (code === IP_NOT_ALLOWED) return IP_NOT_ALLOWED_MESSAGE;

	// Unhandled failures and throttling carry diagnostics, not user text.
	const isGeneric = code === null || GENERIC_ERROR_CODES.has(code);
	if (isGeneric && (status >= 500 || status === 429)) {
		return statusMessage(status);
	}

	return (
		nonEmpty(payload?.message) ??
		nonEmpty(payload?.detail) ??
		statusMessage(status)
	);
}

/** Parse a `Retry-After` header (seconds or HTTP date) into milliseconds. */
function parseRetryAfter(value: unknown): number | undefined {
	if (typeof value !== 'string') return undefined;

	const trimmed = value.trim();

	if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;

	const date = Date.parse(trimmed);
	return Number.isNaN(date) ? undefined : Math.max(0, date - Date.now());
}

/**
 * Whether `value` is already a normalized error. `request()` rejects with one
 * (a plain object); normalizing it again would flatten it to a generic
 * message with no status or code.
 */
function isNormalizedApiError(value: unknown): value is NormalizedApiError {
	return (
		typeof value === 'object' &&
		value !== null &&
		// AxiosError also has status/code/message; it still needs normalizing.
		!(value instanceof Error) &&
		'status' in value &&
		'code' in value &&
		'message' in value
	);
}

/**
 * Convert any thrown value into a predictable, user-safe error. Idempotent:
 * an already normalized error is returned unchanged.
 * Internal diagnostics are preserved on `cause` for logging only.
 */
export function normalizeApiError(error: unknown): NormalizedApiError {
	if (isNormalizedApiError(error)) return error;

	if (error instanceof AxiosError) {
		const response = error.response;

		if (!response) {
			return {
				status: null,
				code: error.code ?? null,
				message: NETWORK_ERROR_MESSAGE,
				cause: error,
			};
		}

		// A proxy may answer with HTML or text instead of Problem Details.
		const payload =
			typeof response.data === 'object' && response.data !== null
				? (response.data as ApiErrorPayload)
				: undefined;
		const code = nonEmpty(payload?.code) ?? null;

		return {
			status: response.status,
			code,
			message: responseMessage(response.status, code, payload),
			fieldErrors: payload?.errors,
			requestId: payload?.requestId,
			retryAfterMs: parseRetryAfter(response.headers?.['retry-after']),
			cause: error,
		};
	}

	if (error instanceof Error) {
		return {
			status: null,
			code: null,
			message: error.message || UNEXPECTED_ERROR_MESSAGE,
			cause: error,
		};
	}

	return {
		status: null,
		code: null,
		message: UNEXPECTED_ERROR_MESSAGE,
		cause: error,
	};
}

/** Whether a failure is the tenant's CMS IP allowlist refusing the request. */
export function isIpNotAllowedError(error: unknown): boolean {
	return normalizeApiError(error).code === IP_NOT_ALLOWED;
}
